import * as React from 'react';
import { cn } from '../../lib/utils';

export interface LiveBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  label?: string;
  /** When false, dims the badge and stops the pulse animation. */
  active?: boolean;
}

/**
 * Live indicator for video tiles and incident headers.
 *
 * Sharp-cornered hairline pill with leading status dot. Pulse honours
 * `prefers-reduced-motion` (drops to a steady tint instead of animation).
 */
function LiveBadge({
  label = 'LIVE',
  active = true,
  className,
  ...props
}: LiveBadgeProps): React.JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-sm border px-1.5 py-0.5',
        'font-mono text-xs uppercase tracking-[0.18em] leading-none tabular-nums',
        active
          ? 'border-[var(--color-red-500)] bg-[var(--color-canvas)]/85 text-[var(--color-foreground)]'
          : 'border-[var(--color-hairline)] bg-[var(--color-canvas)]/85 text-[var(--color-foreground-mute)]',
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          'inline-block size-1.5 rounded-full',
          active
            ? 'bg-[var(--color-red-500)] motion-safe:animate-[cg-pulse_1.6s_ease-out_infinite]'
            : 'bg-[var(--color-graphite-500)]',
        )}
      />
      {label}
    </span>
  );
}

export { LiveBadge };
